import { OrderDetailAttributes } from '@albatrosdeveloper/ave-models-package';
import OrderAttributes from '@albatrosdeveloper/ave-models-package/lib/schemas/process/order/order.entity';

import { customerIdOf, orderIdOf } from './plan-result';
import { basePlanOrderId } from './route-optimization.mapper';

/**
 * Qué pedidos componen una parada del plan, para poder mostrar su detalle.
 *
 * Existe por una razón concreta: el motor devuelve **un** `order_id` por parada,
 * pero con `groupByCustomer` una parada puede ser **varios** pedidos del mismo
 * cliente. `groupOrdersByCustomer` conserva el id del primero y suma los pesos
 * (`route-optimization.mapper.ts:597-603`), así que resolver por ese id mostraría
 * las líneas de uno solo mientras la parada informa el peso de todos — un detalle
 * que contradice su propio encabezado.
 *
 * Se reconstruye **por cliente**: los grupos de planificación son disjuntos por
 * cliente (verificado sobre datos reales), así que "los pedidos de este cliente"
 * es exactamente el contenido de la parada.
 *
 * Módulo puro: entran el id de la parada y las órdenes cargadas, sale el modelo
 * de vista. Sin HTTP, sin estado, sin Angular.
 */

/** Un pedido de la parada, listo para pintar su tabla de materiales. */
export interface StopOrderView {
  orderId: string;
  /** Serial del documento; el id si no lo trae. */
  serial: string;
  weightLb: number;
  /** Líneas de material; vacío si el pedido no las trae. */
  detail: OrderDetailAttributes[];
}

/** Los pedidos de una parada, y si esa parada es una división por capacidad. */
export interface StopOrdersView {
  /**
   * `true` si la parada es una de las partes de una visita dividida (`__sN`).
   *
   * En ese caso el detalle corresponde al **cliente completo**, no a la parte: el
   * bin-packing reparte por peso y no registra qué pedido fue a qué parte
   * (`mapper.ts:620-622`), así que atribuirlos sería inventar.
   */
  split: boolean;
  orders: StopOrderView[];
}

/** Serial del documento, o su id como respaldo. */
function serialOf(order: OrderAttributes): string {
  const serial = (order as unknown as { serial?: string }).serial;
  return serial || orderIdOf(order);
}

/**
 * Pedidos que componen la parada `stopOrderId`.
 *
 * @param stopOrderId `order_id` que devolvió el motor (puede traer sufijo `__sN`).
 * @param orders órdenes cargadas de la corrida.
 * @param weightLbOf peso de entrega de una orden. Se **inyecta** porque depende
 *   del `loadCalculationMode` de la compañía y esa regla ya vive en el diálogo:
 *   recalcularla acá crearía una segunda copia que divergiría.
 *
 * El resultado se ordena por serial para que el panel no cambie de orden entre
 * renders.
 */
export function stopOrders(
  stopOrderId: string,
  orders: readonly OrderAttributes[],
  weightLbOf: (order: OrderAttributes) => number,
): StopOrdersView {
  const baseId = basePlanOrderId(stopOrderId);
  const split = baseId !== stopOrderId;

  const base = (orders ?? []).find((o) => orderIdOf(o) === baseId);
  if (!base) return { split: false, orders: [] };

  const customerId = customerIdOf(base);
  const mine = (orders ?? []).filter((o) => customerIdOf(o) === customerId);

  return {
    split,
    orders: mine
      .map((o) => ({
        orderId: orderIdOf(o),
        serial: serialOf(o),
        weightLb: weightLbOf(o),
        detail: (o.detail ?? []) as OrderDetailAttributes[],
      }))
      .sort((a, b) => a.serial.localeCompare(b.serial)),
  };
}
